import { BrandingConfig } from "@apis:ui_branding/ui_branding";
import { useEffect, useRef } from "react";

const DEFAULT_TITLE = "Nemo";
const DEFAULT_FAVICON =
  "https://static.wixstatic.com/shapes/ad43e3_a25538a1c4574a14afaab43c6b836cad.svg";

/**
 * Hook to apply browser title and favicon from branding configuration.
 * Updates document.title and link[rel="icon"] based on branding settings.
 */
export const useBrowserBranding = (config: BrandingConfig | null) => {
  const originalTitleRef = useRef<string>(DEFAULT_TITLE);
  const originalFaviconRef = useRef<string>(DEFAULT_FAVICON);

  useEffect(() => {
    // Store original values on first mount
    originalTitleRef.current = document.title || DEFAULT_TITLE;
    const existingFavicon =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existingFavicon) {
      originalFaviconRef.current = existingFavicon.href;
    }
  }, []);

  // Apply browser title
  useEffect(() => {
    if (config?.browserTitle) {
      document.title = config.browserTitle;
    } else {
      document.title = originalTitleRef.current;
    }

    return () => {
      document.title = originalTitleRef.current;
    };
  }, [config?.browserTitle]);

  // Apply favicon
  useEffect(() => {
    const faviconUrl = config?.faviconBase64 || config?.faviconUrl;

    if (faviconUrl) {
      updateFavicon(faviconUrl);
    } else {
      updateFavicon(originalFaviconRef.current);
    }

    return () => {
      updateFavicon(originalFaviconRef.current);
    };
  }, [config?.faviconBase64, config?.faviconUrl]);
};

/**
 * Updates or creates the favicon link element
 */
function updateFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.setAttribute("sizes", "192x192");
    document.head.appendChild(link);
  }

  link.href = href;
}
